import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import api, { fmtMoney, fmtDate, formatApiError, fmtNum } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import AttachmentsPanel from "../components/AttachmentsPanel";
import { Button } from "../components/ui/button";
import {
    ArrowLeft, CheckCircle2, XCircle, RefreshCcw, AlertTriangle,
    FileText, PackageCheck, Receipt, Loader2, Check, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/rbac";

export default function InvoiceDetail() {
    const { user } = useAuth();
    const canApprove = can(user, "invoice.approve");
    const canReject = can(user, "invoice.reject");
    const canRematch = can(user, "invoice.create");
    const { id } = useParams();
    const nav = useNavigate();
    const [inv, setInv] = useState(null);
    const [po, setPo] = useState(null);
    const [grs, setGrs] = useState([]);

    const load = async () => {
        const invR = await api.get(`/invoices/${id}`);
        setInv(invR.data);
        const [poR, grR] = await Promise.all([
            api.get(`/purchase-orders/${invR.data.po_id}`),
            api.get(`/goods-receipts?po_id=${invR.data.po_id}`),
        ]);
        setPo(poR.data); setGrs(grR.data);
    };
    useEffect(() => { load(); }, [id]);

    if (!inv || !po) return <AppShell title="Fatura"><div className="py-20 text-center"><Loader2 className="animate-spin inline"/></div></AppShell>;

    const rematch = async () => {
        try { await api.post(`/invoices/${id}/rematch`); toast.success("Yeniden eşleştirildi"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const approve = async () => {
        try { await api.post(`/invoices/${id}/approve`); toast.success("Fatura onaylandı"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const reject = async () => {
        try { await api.post(`/invoices/${id}/reject`, { reason: "Uyumsuzluk" }); toast.success("Fatura reddedildi"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    // Aggregate received qty per PO line
    const received = {};
    grs.forEach(g => g.lines.forEach(l => {
        received[l.po_line_id] = (received[l.po_line_id] || 0) + (l.accepted_qty || 0);
    }));

    const canApprove_final = inv.match_status === "matched" && (inv.status === "matched" || inv.status === "draft");

    return (
        <AppShell
            title={`Fatura ${inv.number}`}
            subtitle="3'lü Eşleştirme"
            actions={
                <div className="flex gap-2">
                    {canRematch && <Button variant="outline" onClick={rematch} data-testid="inv-rematch"><RefreshCcw size={14} className="mr-2"/> Yeniden Eşleştir</Button>}
                    {canApprove_final && canApprove && <Button data-testid="inv-approve" onClick={approve}><CheckCircle2 size={14} className="mr-2"/> Onayla</Button>}
                    {inv.status === "draft" && !canApprove_final && canReject && <Button variant="destructive" onClick={reject} data-testid="inv-reject"><XCircle size={14} className="mr-2"/> Reddet</Button>}
                </div>
            }
        >
            <Button variant="ghost" size="sm" onClick={()=>nav("/invoices")} className="mb-4"><ArrowLeft size={14} className="mr-2"/> Faturalar</Button>

            {/* Match Status banner */}
            <div className={`border rounded-md p-4 mb-4 ${
                inv.match_status === "matched" ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900" :
                inv.match_status === "mismatch" ? "bg-rose-50 border-rose-200 dark:bg-rose-950/20 dark:border-rose-900" :
                "bg-muted border-border"
            }`}>
                <div className="flex items-center gap-3">
                    {inv.match_status === "matched" ? <CheckCircle2 className="text-emerald-600" size={22}/> :
                     inv.match_status === "mismatch" ? <AlertTriangle className="text-rose-600" size={22}/> : null}
                    <div className="flex-1">
                        <div className="font-semibold">
                            {inv.match_status === "matched" ? "3'lü Eşleştirme Başarılı" :
                             inv.match_status === "mismatch" ? "3'lü Eşleştirme Uyumsuz" : "Eşleşme Yapılmadı"}
                        </div>
                        <div className="text-sm text-muted-foreground mt-0.5">
                            {inv.discrepancies?.length ?
                                `${inv.discrepancies.length} uyumsuzluk tespit edildi` :
                                "PO, Mal Kabul ve Fatura arasında tolerans içinde uyum var"}
                        </div>
                    </div>
                    <StatusBadge status={inv.status}/>
                </div>
            </div>

            {/* Three columns: PO / GR / Invoice */}
            <div className="grid grid-cols-3 gap-1 mb-6" data-testid="three-way-visual">
                <ThreeCol title="Sipariş (PO)" icon={FileText} value={po.number} sub={fmtDate(po.date)} amount={fmtMoney(po.total_amount, po.currency)} tone="blue"/>
                <ThreeCol title="Mal Kabul" icon={PackageCheck} value={`${grs.length} kayıt`} sub={grs.length ? fmtDate(grs[grs.length-1].date) : "—"} amount={`${grs.reduce((a,g)=>a+g.lines.reduce((b,l)=>b+l.accepted_qty,0), 0)} adet`} tone="amber"/>
                <ThreeCol title="Fatura" icon={Receipt} value={inv.supplier_invoice_number} sub={fmtDate(inv.invoice_date)} amount={fmtMoney(inv.total_amount, inv.currency)} tone={inv.match_status === "matched" ? "green" : "rose"}/>
            </div>

            {/* Line-by-line matching */}
            <div className="bg-card border border-border rounded-md mb-6">
                <div className="p-4 border-b border-border">
                    <div className="text-sm font-medium">Satır Bazlı Karşılaştırma</div>
                </div>
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th rowSpan={2}>Ürün</th>
                                <th colSpan={2} className="text-center border-r border-border" style={{ background: "hsl(220 90% 96%)" }}>PO (Sipariş)</th>
                                <th colSpan={1} className="text-center border-r border-border" style={{ background: "hsl(38 92% 95%)" }}>Mal Kabul</th>
                                <th colSpan={2} className="text-center border-r border-border" style={{ background: "hsl(152 40% 94%)" }}>Fatura</th>
                                <th rowSpan={2}>Durum</th>
                            </tr>
                            <tr>
                                <th className="text-right" style={{ background: "hsl(220 90% 96%)" }}>Miktar</th>
                                <th className="text-right border-r border-border" style={{ background: "hsl(220 90% 96%)" }}>Fiyat</th>
                                <th className="text-right border-r border-border" style={{ background: "hsl(38 92% 95%)" }}>Kabul</th>
                                <th className="text-right" style={{ background: "hsl(152 40% 94%)" }}>Miktar</th>
                                <th className="text-right border-r border-border" style={{ background: "hsl(152 40% 94%)" }}>Fiyat</th>
                            </tr>
                        </thead>
                        <tbody>
                            {po.lines.map(pl => {
                                const invLine = inv.lines.find(il => il.po_line_id === pl.line_id || il.product_id === pl.product_id);
                                const rec = received[pl.line_id] || 0;
                                const lineDiscs = inv.discrepancies?.filter(d => d.sku === pl.sku) || [];
                                const hasErr = lineDiscs.some(d => d.severity === "error");
                                const hasWarn = lineDiscs.some(d => d.severity === "warning");
                                return (
                                    <tr key={pl.line_id} data-testid={`match-line-${pl.sku}`}>
                                        <td>
                                            <div className="text-xs font-mono">{pl.sku}</div>
                                            <div className="text-sm">{pl.name}</div>
                                        </td>
                                        <td className="text-right tabular-nums bg-blue-50/50 dark:bg-blue-950/10">{fmtNum(pl.quantity, 0)}</td>
                                        <td className="text-right tabular-nums bg-blue-50/50 dark:bg-blue-950/10 border-r border-border">{fmtMoney(pl.unit_price, po.currency)}</td>
                                        <td className={`text-right tabular-nums border-r border-border ${rec ? "bg-amber-50/50 dark:bg-amber-950/10" : "bg-rose-50 text-rose-700 dark:bg-rose-950/20"}`}>{fmtNum(rec, 0)}</td>
                                        <td className={`text-right tabular-nums ${invLine?.quantity && rec && Math.abs(invLine.quantity - rec) > 0.01 ? "bg-rose-50 text-rose-700 dark:bg-rose-950/20 font-medium" : "bg-emerald-50/50 dark:bg-emerald-950/10"}`}>{invLine ? fmtNum(invLine.quantity, 0) : "—"}</td>
                                        <td className={`text-right tabular-nums border-r border-border ${invLine?.unit_price && Math.abs(invLine.unit_price - pl.unit_price) > 0.01 ? "bg-rose-50 text-rose-700 dark:bg-rose-950/20 font-medium" : "bg-emerald-50/50 dark:bg-emerald-950/10"}`}>{invLine ? fmtMoney(invLine.unit_price, inv.currency) : "—"}</td>
                                        <td>
                                            {hasErr ? <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-medium"><X size={14}/> Uyumsuz</span> :
                                             hasWarn ? <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium"><AlertTriangle size={14}/> Uyarı</span> :
                                             <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><Check size={14}/> Uyumlu</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Discrepancies detail */}
            {inv.discrepancies?.length > 0 && (
                <div className="bg-card border border-border rounded-md mb-4" data-testid="discrepancies">
                    <div className="p-4 border-b border-border">
                        <div className="text-sm font-medium flex items-center gap-2"><AlertTriangle className="text-rose-600" size={16}/> Uyumsuzluk Detayı</div>
                    </div>
                    <div className="divide-y divide-border">
                        {inv.discrepancies.map((d, i) => (
                            <div key={i} className="p-4 flex items-start gap-3">
                                <div className={`w-2 h-2 rounded-full mt-2 ${d.severity === "error" ? "bg-rose-500" : d.severity === "warning" ? "bg-amber-500" : "bg-blue-500"}`}/>
                                <div className="flex-1">
                                    <div className="text-sm font-medium">{d.message}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        Tip: {d.type} • {d.sku && `Ürün: ${d.sku}`}
                                        {d.diff_pct !== null && d.diff_pct !== undefined && ` • Fark: %${d.diff_pct}`}
                                    </div>
                                </div>
                                <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider font-medium ${d.severity === "error" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                                    {d.severity}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <AttachmentsPanel entityType="invoice" entityId={inv.id}/>
        </AppShell>
    );
}

function ThreeCol({ title, icon: Icon, value, sub, amount, tone }) {
    const toneMap = {
        blue: "border-l-blue-500",
        amber: "border-l-amber-500",
        green: "border-l-emerald-500",
        rose: "border-l-rose-500",
    };
    return (
        <div className={`bg-card border border-border border-l-4 ${toneMap[tone]} rounded-md p-5`}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
                <Icon size={14}/> {title}
            </div>
            <div className="text-lg font-mono font-semibold mt-2">{value}</div>
            <div className="text-xs text-muted-foreground">{sub}</div>
            <div className="text-xl font-semibold tabular-nums mt-3">{amount}</div>
        </div>
    );
}
